import type {
  AudienceDelivery,
  AudienceDestination,
  AudienceDestinationStatus,
  DestinationPlatform
} from '../types';
import {
  isDeliveryUnfinished,
  isLiveDestination,
  latestDeliveryByDestination
} from './deriveAudienceStatus';

export type PlatformDeliverySummary = {
  platform: DestinationPlatform;
  liveCount: number;
  /** The least-progressed live status: pending, then populating, then active. */
  worstStatus: AudienceDestinationStatus;
};

const STATUS_RANK: Record<AudienceDestinationStatus, number> = {
  pending: 0,
  populating: 1,
  active: 2,
  deleted: 3
};

export const PLATFORM_LABELS: Record<DestinationPlatform, string> = {
  meta: 'Meta',
  tiktok: 'TikTok',
  google: 'Google'
};

const PLATFORM_ORDER: DestinationPlatform[] = ['meta', 'tiktok', 'google'];

/** Live destinations counted per platform, each with its worst status, in platform order. */
export const summarizeDeliveries = (
  destinations: AudienceDestination[]
): PlatformDeliverySummary[] =>
  PLATFORM_ORDER.flatMap(platform => {
    const live = destinations.filter(row => row.platform === platform && isLiveDestination(row));
    if (live.length === 0) return [];
    const worstStatus = live.reduce<AudienceDestinationStatus>(
      /* An unrecognized status ranks as least progressed */
      (worst, row) =>
        (STATUS_RANK[row.status] ?? -1) < (STATUS_RANK[worst] ?? -1) ? row.status : worst,
      'active'
    );
    return [{ platform, liveCount: live.length, worstStatus }];
  });

/** Destinations grouped by platform for the detail page, live and deleted, in platform order. */
export const groupByPlatform = (
  destinations: AudienceDestination[]
): { platform: DestinationPlatform; destinations: AudienceDestination[] }[] =>
  PLATFORM_ORDER.flatMap(platform => {
    const rows = destinations.filter(row => row.platform === platform);
    return rows.length > 0 ? [{ platform, destinations: rows }] : [];
  });

/**
 * Whether a live destination is still moving without user action.
 *
 * A destination moves while it is pending or populating, or while its latest delivery in `deliveries` is unfinished.
 */
export const hasDestinationsInFlight = (
  destinations: AudienceDestination[],
  deliveries: AudienceDelivery[] = []
): boolean => {
  const latest = latestDeliveryByDestination(deliveries);
  return destinations.some(row => {
    if (!isLiveDestination(row)) return false;
    if (row.status === 'pending' || row.status === 'populating') return true;
    const delivery = latest.get(row.id);
    return delivery != null && isDeliveryUnfinished(delivery);
  });
};

/** A platform's display name; one the fixtures or service add later shows as its raw value. */
export const platformLabel = (platform: DestinationPlatform): string =>
  PLATFORM_LABELS[platform] ?? platform;
